import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { i18n, staffFeatureAllowed } from '@wms/shared';
import { useAuth } from '../lib/auth-context.js';
import { canWriteStaffScope } from '../lib/staff-access.js';
import { api, ApiError } from '../lib/api.js';
import { PhotoGallery } from '../components/PhotoGallery.js';
import { EditSkuDialog } from '../components/EditSkuDialog.js';
import { CreateProductFromComponentDialog } from '../components/CreateProductFromComponentDialog.js';
import { SearchSelect } from '../components/SearchSelect.js';
import { NumberInput } from '../components/NumberInput.js';
import { ThumbButton } from '../components/ThumbButton.js';

const { t } = i18n;

interface SkuAttr {
  key: string;
  value: string;
}

interface BomLineRow {
  id: string;
  componentSkuId: string;
  qtyRequired: string; // Prisma Decimal serializes as string
  sortOrder: number;
  comment: string | null;
  component: {
    id: string;
    code: string;
    nameLt: string;
    type: 'PRODUCT' | 'COMPONENT';
    photoUrl: string | null;
  };
}

interface SkuLocationChange {
  id: string;
  fromRow: string | null;
  fromShelf: string | null;
  fromPosition: string | null;
  toRow: string | null;
  toShelf: string | null;
  toPosition: string | null;
  reason: string | null;
  timestamp: string;
}

interface SkuDetail {
  id: string;
  partnerId: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  nameEn: string | null;
  attrs: SkuAttr[];
  slaOverrideWorkdays: number | null;
  lowWaterQty: number | null;
  notes: string | null;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  locationChanges: SkuLocationChange[];
  bomLines: BomLineRow[];
  maxProducible: { units: number; bottlenecks: string[] };
}

interface ComponentRow {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

interface DraftLine {
  componentSkuId: string;
  qtyRequired: number;
  sortOrder: number;
}

export function SkuDetailPage() {
  const { partnerId = '', skuId = '' } = useParams();
  const { user } = useAuth();
  const writable = canWriteStaffScope(user, 'PARTNER', partnerId);
  const broadWrite =
    writable && Boolean(user?.roles.some((r) => ['ADMIN', 'PARTNER_ADMIN'].includes(r.role)));
  const canCreate = writable && staffFeatureAllowed(user, 'skuCreate');
  const canEdit = writable && staffFeatureAllowed(user, 'skuEdit');
  const canUpload = writable && staffFeatureAllowed(user, 'skuPhotoUpload');
  const navigate = useNavigate();
  const [sku, setSku] = useState<SkuDetail | null>(null);
  const [components, setComponents] = useState<ComponentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editSkuOpen, setEditSkuOpen] = useState(false);
  const [createProductOpen, setCreateProductOpen] = useState(false);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setSku(null);
    api<SkuDetail>(`/partners/${partnerId}/skus/${skuId}`)
      .then(setSku)
      .catch((err: unknown) => setError(String(err)));
  }, [partnerId, skuId, reloadKey]);

  useEffect(() => {
    api<ComponentRow[]>(`/partners/${partnerId}/skus`)
      .then((all) => setComponents(all.filter((s) => s.type === 'COMPONENT' && s.id !== skuId)))
      .catch(() => setComponents([]));
  }, [partnerId, skuId, reloadKey]);

  const componentsById = useMemo(() => {
    const map = new Map<string, ComponentRow>();
    for (const c of components) map.set(c.id, c);
    return map;
  }, [components]);

  function startEdit() {
    if (!sku) return;
    setDraft(
      sku.bomLines.map((l) => ({
        componentSkuId: l.componentSkuId,
        qtyRequired: Number(l.qtyRequired),
        sortOrder: l.sortOrder,
      })),
    );
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft([]);
  }

  function addLine() {
    if (components.length === 0) return;
    setDraft((d) => [
      ...d,
      {
        componentSkuId: '',
        qtyRequired: 1,
        sortOrder: d.length,
      },
    ]);
  }

  async function save() {
    setSaving(true);
    try {
      await api(`/partners/${partnerId}/skus/${skuId}/bom`, {
        method: 'PUT',
        body: { lines: draft },
      });
      setEditing(false);
      setReloadKey((k) => k + 1);
    } catch {
      setError(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  // Jonas #1 (2026-06-18): hard-delete when unused, otherwise the API returns
  // `sku_in_use` and we tell the user to archive instead.
  async function handleDelete() {
    if (!window.confirm(t('sku.delete.confirm'))) return;
    setActionError(null);
    try {
      await api(`/partners/${partnerId}/skus/${skuId}`, { method: 'DELETE' });
      navigate(`/partners/${partnerId}`);
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.code === 'sku_in_use'
          ? t('sku.delete.in_use')
          : t('common.error'),
      );
    }
  }

  async function handleClone() {
    setActionError(null);
    try {
      const clone = await api<{ id: string }>(`/partners/${partnerId}/skus/${skuId}/clone`, {
        method: 'POST',
      });
      navigate(`/partners/${partnerId}/skus/${clone.id}`);
    } catch {
      setActionError(t('common.error'));
    }
  }

  async function handleArchive() {
    if (!window.confirm(t('sku.archive.confirm'))) return;
    setActionError(null);
    try {
      await api(`/partners/${partnerId}/skus/${skuId}`, {
        method: 'PATCH',
        body: { status: 'ARCHIVED' },
      });
      navigate(`/partners/${partnerId}`);
    } catch {
      setActionError(t('common.error'));
    }
  }

  async function handleConvert(targetType: 'PRODUCT' | 'COMPONENT') {
    if (!window.confirm(t('sku.convert.confirm'))) return;
    setActionError(null);
    try {
      await api(`/partners/${partnerId}/skus/${skuId}/convert`, {
        method: 'POST',
        body: { targetType },
      });
      setReloadKey((k) => k + 1);
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.code === 'sku_in_orders'
          ? t('sku.convert.in_orders')
          : t('common.error'),
      );
    }
  }

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!sku) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link
            to={`/partners/${partnerId}`}
            className="text-xs text-muted-foreground hover:underline"
          >
            ← {t('partner.list.title')}
          </Link>
          <h2 className="text-xl font-semibold mt-1">
            <span className="font-mono text-base text-muted-foreground mr-2">{sku.code}</span>
            {sku.nameLt}
          </h2>
          {sku.nameEn ? <div className="text-sm text-muted-foreground">{sku.nameEn}</div> : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {sku.type === 'COMPONENT' && broadWrite && canCreate ? (
              <button
                type="button"
                onClick={() => setCreateProductOpen(true)}
                className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
              >
                {t('sku.from_component.action')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void handleConvert(sku.type === 'COMPONENT' ? 'PRODUCT' : 'COMPONENT')}
              disabled={!broadWrite}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {sku.type === 'COMPONENT'
                ? t('sku.convert.to_product')
                : t('sku.convert.to_component')}
            </button>
            {sku.type === 'PRODUCT' && broadWrite && canCreate ? (
              <button
                type="button"
                onClick={() => void handleClone()}
                className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
              >
                {t('sku.clone.action')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setEditSkuOpen(true)}
              disabled={!canEdit}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('sku.edit.action')}
            </button>
            <button
              type="button"
              onClick={() => void handleArchive()}
              disabled={!broadWrite}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('sku.archive.action')}
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={!broadWrite}
              className="rounded border border-red-300 text-red-700 px-3 py-1.5 text-sm hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950"
            >
              {t('common.delete')}
            </button>
          </div>
          {actionError ? (
            <div className="text-xs text-red-600 text-right max-w-xs">{actionError}</div>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4 text-sm">
        <Info label={t('sku.col.type')}>{t(`sku.type.${sku.type}`)}</Info>
        <Info label={t('sku.col.sla_override')}>
          {sku.slaOverrideWorkdays !== null
            ? `${sku.slaOverrideWorkdays} ${t('partner.col.sla_unit')}`
            : t('sku.col.sla_inherits')}
        </Info>
        <Info label={t('sku.col.low_water')}>{sku.lowWaterQty ?? '—'}</Info>
        <Info label={t('location.label')}>
          <span className="font-mono">{formatSkuLocation(sku)}</span>
        </Info>
      </div>

      <div className="rounded border border-border p-3 text-sm">
        <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
          {t('sku.edit.notes')}
        </div>
        <p className="whitespace-pre-wrap">{sku.notes || '—'}</p>
      </div>

      {sku.locationChanges.length > 0 ? (
        <div className="rounded border border-border p-3 text-xs">
          <div className="mb-1 uppercase tracking-wide text-muted-foreground">
            {t('location.history_title')}
          </div>
          <ul className="space-y-0.5 text-muted-foreground">
            {sku.locationChanges.map((c) => {
              const from =
                [c.fromRow, c.fromShelf, c.fromPosition]
                  .filter((x): x is string => Boolean(x))
                  .join('-') || '—';
              const to =
                [c.toRow, c.toShelf, c.toPosition]
                  .filter((x): x is string => Boolean(x))
                  .join('-') || '—';
              const when = c.timestamp.slice(0, 10);
              return (
                <li key={c.id} className="font-mono">
                  {from} → {to}
                  <span className="ml-2 font-sans text-[11px]">
                    {when}
                    {c.reason ? ` · ${c.reason}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {sku.attrs.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {sku.attrs.map((a, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
            >
              <span className="font-medium">{a.key}</span>
              <span className="mx-1 text-muted-foreground">·</span>
              <span>{a.value}</span>
            </span>
          ))}
        </div>
      ) : null}

      <PhotoGallery
        basePath={`/partners/${partnerId}/skus/${skuId}/photos`}
        canEdit={broadWrite}
        canUpload={canUpload}
        reloadKey={reloadKey}
      />

      {sku.type === 'PRODUCT' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('bom.title')}
            </h3>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs">
                {t('bom.max_producible')}:{' '}
                <strong className="tabular-nums">{sku.maxProducible.units}</strong>
              </span>
              {!editing ? (
                <button
                  type="button"
                  onClick={startEdit}
                  disabled={!broadWrite}
                  className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
                >
                  {t('bom.edit')}
                </button>
              ) : null}
            </div>
          </div>

          {editing ? (
            <div className="space-y-2">
              {draft.length === 0 ? (
                <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                  {t('bom.draft_empty')}
                </div>
              ) : null}
              {draft.map((line, i) => {
                const comp = componentsById.get(line.componentSkuId);
                return (
                  <div key={i} className="flex items-center gap-2 rounded border border-border p-2">
                    <SearchSelect
                      className="flex-1 text-sm"
                      ariaLabel={t('bom.pick_component')}
                      value={line.componentSkuId}
                      onChange={(id) =>
                        setDraft((d) =>
                          d.map((l, j) => (j === i ? { ...l, componentSkuId: id } : l)),
                        )
                      }
                      options={components.map((c) => ({
                        id: c.id,
                        label: `${c.code} — ${c.nameLt}`,
                        imageUrl: c.photoUrl,
                        availableQty: c.availableQty,
                        maxProducibleQty: c.maxProducibleQty ?? undefined,
                      }))}
                    />
                    <NumberInput
                      integer
                      min={1}
                      value={line.qtyRequired}
                      onChange={(v) =>
                        setDraft((d) =>
                          d.map((l, j) => (j === i ? { ...l, qtyRequired: v ?? 0 } : l)),
                        )
                      }
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm tabular-nums"
                    />
                    <span className="text-xs text-muted-foreground" title={comp?.nameLt ?? ''}>
                      {comp?.code ?? '?'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}
                      className="rounded border border-border px-2 py-1 text-xs"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={addLine}
                  disabled={components.length === 0 || draft.some((line) => !line.componentSkuId)}
                  className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  {t('bom.add_line')}
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="rounded border border-border px-3 py-1.5 text-sm"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void save()}
                    disabled={
                      saving || draft.some((line) => !line.componentSkuId || line.qtyRequired <= 0)
                    }
                    className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm disabled:opacity-50"
                  >
                    {saving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </div>
              {components.length === 0 ? (
                <div className="text-xs text-muted-foreground">{t('bom.no_components')}</div>
              ) : null}
            </div>
          ) : sku.bomLines.length === 0 ? (
            <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              {t('bom.empty')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('bom.col.component')}</th>
                    <th className="px-3 py-2 font-medium">{t('bom.col.qty')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sku.bomLines.map((line) => (
                    <tr key={line.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <ThumbButton
                            url={line.component.photoUrl}
                            alt={`${line.component.code} — ${line.component.nameLt}`}
                          />
                          <span>
                            <span className="font-mono text-xs">{line.component.code}</span>
                            <span className="ml-2">{line.component.nameLt}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {Number(line.qtyRequired).toLocaleString('lt-LT')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {editSkuOpen ? (
        <EditSkuDialog
          partnerId={partnerId}
          skuId={skuId}
          canTransfer={broadWrite}
          canCopy={canCreate}
          initial={{
            code: sku.code,
            nameLt: sku.nameLt,
            nameEn: sku.nameEn,
            attrs: sku.attrs,
            slaOverrideWorkdays: sku.slaOverrideWorkdays,
            lowWaterQty: sku.lowWaterQty,
            notes: sku.notes,
            locationRow: sku.locationRow,
            locationShelf: sku.locationShelf,
            locationPosition: sku.locationPosition,
          }}
          onClose={() => setEditSkuOpen(false)}
          onSaved={() => {
            setEditSkuOpen(false);
            setReloadKey((k) => k + 1);
          }}
          onTransferred={(result) => {
            setEditSkuOpen(false);
            navigate(`/partners/${result.partnerId}/skus/${result.skuId}`);
          }}
        />
      ) : null}

      {createProductOpen ? (
        <CreateProductFromComponentDialog
          partnerId={partnerId}
          component={{ id: sku.id, code: sku.code, nameLt: sku.nameLt, nameEn: sku.nameEn }}
          onClose={() => setCreateProductOpen(false)}
          onCreated={(productId) => {
            setCreateProductOpen(false);
            navigate(`/partners/${partnerId}/skus/${productId}`);
          }}
        />
      ) : null}
    </section>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function formatSkuLocation(s: {
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}): string {
  const parts = [s.locationRow, s.locationShelf, s.locationPosition].filter((x): x is string =>
    Boolean(x && x.length > 0),
  );
  return parts.length === 0 ? '—' : parts.join('-');
}
